import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { FirebaseService } from './firebase.service.js';

/**
 * Per-tier quota defaults stored in `system/quota-defaults` Firestore doc.
 * `null` = unlimited (no enforcement); `0` = blocked. Mirror of
 * `cloud-run-api/lib/quota.js` HARDCODED_DEFAULTS.
 */
export interface QuotaDefaults {
    free: { aiQuick: number | null; aiDeep: number | null; aiSearch: number | null; clip: number | null; zip: number | null };
    pro:  { aiQuick: number | null; aiDeep: number | null; aiSearch: number | null; clip: number | null; zip: number | null };
}

const HARDCODED_DEFAULTS: QuotaDefaults = {
    free: { aiQuick: 10, aiDeep: 2, aiSearch: 5, clip: 20, zip: 0 },
    pro:  { aiQuick: 100, aiDeep: 20, aiSearch: 30, clip: null, zip: null },
};

export type QuotaKind = 'aiQuick' | 'aiDeep' | 'aiSearch' | 'clip' | 'zip';

/**
 * Thrown by gemini.service / download.service when the server responds with
 * 429 + body `{error: 'QUOTA_EXCEEDED', kind, limit, resetAt}`. The catch
 * site (AppComponent handlers) reads the fields to show a localized banner
 * or the Pro upsell modal.
 */
export class QuotaExceededError extends Error {
    constructor(
        public kind: QuotaKind,
        public limit: number,
        public resetAtIso: string | null,
    ) {
        super('QUOTA_EXCEEDED');
        this.name = 'QuotaExceededError';
    }
}

const COUNTER_FIELD: Record<QuotaKind, string> = {
    aiQuick: 'dailyAiQuickCount',
    aiDeep: 'dailyAiDeepCount',
    aiSearch: 'dailyAiSearchCount',
    clip: 'dailyClipCount',
    zip: 'dailyZipCount',
};
const OVERRIDE_FIELD: Record<QuotaKind, string> = {
    aiQuick: 'quotaAiQuickOverride',
    aiDeep: 'quotaAiDeepOverride',
    aiSearch: 'quotaAiSearchOverride',
    clip: 'quotaClipOverride',
    zip: 'quotaZipOverride',
};

export interface QuotaState {
    /** Times the user has used this op today (success-only). */
    used: number;
    /** null = unlimited; integer = hard cap. */
    limit: number | null;
    /** `Infinity` if unlimited; else `max(0, limit - used)`. */
    remaining: number;
    /** True when limit !== null && used >= limit. */
    exhausted: boolean;
    /** True when this kind has a per-user override (limit doesn't follow tier). */
    hasOverride: boolean;
}

@Injectable({ providedIn: 'root' })
export class QuotaService {
    private firebase = inject(FirebaseService);

    /** Live tier defaults from `system/quota-defaults`. Falls back to hardcoded. */
    defaults = signal<QuotaDefaults>(HARDCODED_DEFAULTS);

    /**
     * Live raw user doc — same `users/{uid}` Firestore doc that `loadUserProfile`
     * reads one-shot on login. We subscribe separately so quota counters update
     * after each AI / clip call without needing to refresh the page.
     */
    userQuotaRaw = signal<Record<string, any> | null>(null);

    /**
     * 1-minute tick read by `computeKind` so the `now >= resetAt` short-circuit
     * re-evaluates without depending on a Firestore mutation. Without this, a tab
     * left open across UTC midnight would show stale "exhausted" badges until the
     * user hard-reloaded — `Date.now()` isn't reactive, and no other dependency
     * fires after midnight when the user can't even click anything.
     */
    private minuteTick = signal(0);

    private unsubDefaults: (() => void) | null = null;
    private unsubUser: (() => void) | null = null;
    private currentSubUid: string | null = null;

    constructor() {
        // Wait for Firestore handle to initialize, then subscribe to defaults.
        effect(() => {
            const ready = this.firebase.isInitialized();
            if (!ready) return;
            if (this.unsubDefaults) return; // already subscribed
            const fs = this.firebase.firestore;
            if (!fs) return;
            this.unsubDefaults = onSnapshot(
                doc(fs, 'system', 'quota-defaults'),
                (snap) => {
                    if (!snap.exists()) {
                        this.defaults.set(HARDCODED_DEFAULTS);
                        return;
                    }
                    const data = snap.data() as Partial<QuotaDefaults>;
                    this.defaults.set({
                        free: { ...HARDCODED_DEFAULTS.free, ...(data.free || {}) },
                        pro: { ...HARDCODED_DEFAULTS.pro, ...(data.pro || {}) },
                    });
                },
                (err) => {
                    console.warn('[quota] system/quota-defaults subscription failed:', err.message);
                    this.defaults.set(HARDCODED_DEFAULTS);
                },
            );
        });

        // Subscribe to current user's doc; re-subscribe when uid changes; tear
        // down on logout. The doc is the same one loaded by `loadUserProfile`
        // (one-shot) — we add an onSnapshot for live counter updates.
        effect(() => {
            // Tracking isInitialized so we re-run after Firestore handle exists.
            const ready = this.firebase.isInitialized();
            const uid = this.firebase.currentUser()?.uid || null;
            const fs = this.firebase.firestore;
            if (!ready) return;
            if (uid === this.currentSubUid && this.unsubUser) return;

            // Tear down previous subscription.
            if (this.unsubUser) {
                try { this.unsubUser(); } catch (_) {}
                this.unsubUser = null;
            }
            this.currentSubUid = uid;
            this.userQuotaRaw.set(null);

            if (!uid || !fs) return;
            this.unsubUser = onSnapshot(
                doc(fs, 'users', uid),
                (snap) => {
                    this.userQuotaRaw.set(snap.exists() ? (snap.data() || {}) : null);
                },
                (err) => {
                    console.warn('[quota] user doc subscription failed:', err.message);
                },
            );
        });

        // 1-minute tick — fires the computed re-eval that catches UTC-midnight reset
        // even on tabs left open. Browser throttles setInterval on hidden tabs (often
        // to ≥1/min anyway, sometimes much slower), so we pair it with a visibility
        // listener that nudges the tick on tab-return — that covers "open yesterday,
        // come back this morning, see exhausted limit" without forcing a reload.
        if (typeof window !== 'undefined') {
            setInterval(() => this.minuteTick.update(v => v + 1), 60_000);
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'visible') {
                        this.minuteTick.update(v => v + 1);
                    }
                });
            }
        }
    }

    /** True when the system has at least one snapshot for both defaults + user. */
    isReady = computed(() => this.userQuotaRaw() !== null);

    /** Date of next UTC midnight reset, or null if user doc not loaded. */
    resetAt = computed<Date | null>(() => {
        const raw = this.userQuotaRaw();
        if (!raw) return null;
        const r = raw['dailyQuotaResetAt'];
        if (!r) {
            // Lazy-init has not happened server-side yet — show today's UTC midnight.
            // Server resets on first usage; the badge stays sane meanwhile.
            return this.computeNextUtcMidnight();
        }
        if (r instanceof Timestamp) return r.toDate();
        if (typeof (r as any).toDate === 'function') return (r as any).toDate();
        if (r instanceof Date) return r;
        return null;
    });

    aiQuickQuota  = computed<QuotaState | null>(() => this.computeKind('aiQuick'));
    aiDeepQuota   = computed<QuotaState | null>(() => this.computeKind('aiDeep'));
    aiSearchQuota = computed<QuotaState | null>(() => this.computeKind('aiSearch'));
    clipQuota     = computed<QuotaState | null>(() => this.computeKind('clip'));
    zipQuota      = computed<QuotaState | null>(() => this.computeKind('zip'));

    private computeKind(kind: QuotaKind): QuotaState | null {
        // Subscribe to the minute tick so the `now >= resetAt` check below re-evaluates
        // when the wall clock crosses UTC midnight even without a Firestore mutation.
        this.minuteTick();
        const raw = this.userQuotaRaw();
        if (raw === null) return null;
        const defaults = this.defaults();
        const isPro = raw['isPro'] === true || this.firebase.isPro();
        const tier = isPro ? 'pro' : 'free';
        const override = raw[OVERRIDE_FIELD[kind]];
        const hasOverride = override !== null && override !== undefined;
        const limit: number | null = hasOverride
            ? (override === null ? null : Number(override))
            : defaults[tier][kind];
        // Local UTC-midnight short-circuit: server-side lazy reset only fires on the
        // next incoming /analyze-* / /download-* request, but a quota-exhausted user
        // can't send one — buttons are disabled by `exhausted=true`, deadlocking the
        // reset. Show a fresh 0 once `now >= resetAt`; the first click after midnight
        // hits checkAndReserveQuota which commits the actual reset to Firestore +
        // increments to 1, after which the onSnapshot reflects truth.
        const resetAtDate = this.resetAt();
        const isStaleReset = resetAtDate !== null && Date.now() >= resetAtDate.getTime();
        const used = isStaleReset ? 0 : Number(raw[COUNTER_FIELD[kind]] || 0);

        if (limit === null) {
            return { used, limit: null, remaining: Infinity, exhausted: false, hasOverride };
        }
        const remaining = Math.max(0, limit - used);
        return { used, limit, remaining, exhausted: used >= limit, hasOverride };
    }

    private computeNextUtcMidnight(): Date {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    }
}
